<template>
  <v-dialog :value="open" max-width="820" persistent @input="$emit('close')">
    <v-card class="si-card">

      <v-card-title class="si-title">
        <span class="material-symbols-outlined">download</span>
        Importer les mesures d'un autre univers
        <v-spacer></v-spacer>
        <v-btn icon small @click="$emit('close')">
          <v-icon small>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Étape 1 : choix de la source -->
      <v-card-text v-if="step === 1" class="si-body">
        <p class="si-intro">
          Les mesures de l'univers choisi sont recopiées dans celui-ci. Rien
          n'est déplacé ni modifié côté source, et aucune donnée Elasticsearch
          n'est touchée : seule la définition des mesures est dupliquée.
        </p>

        <div v-if="!candidates.length" class="si-empty">
          Aucun autre univers disponible chez ce client.
        </div>

        <v-select
            v-else
            v-model="sourceId"
            :items="candidates"
            item-text="name"
            item-value="id"
            label="Univers source"
            outlined dense hide-details
            prepend-inner-icon="domain"
        ></v-select>

        <div v-if="sourceSite" class="si-source-info">
          <span class="material-symbols-outlined">sensors</span>
          {{ sourceSensors.length }} mesure(s) dans « {{ sourceSite.name }} »
        </div>
      </v-card-text>

      <!-- Étape 2 : confirmation -->
      <v-card-text v-else class="si-body">
        <div class="si-summary">
          <div class="si-stat si-stat--add">
            <strong>{{ toImport.length }}</strong>
            <span>à importer</span>
          </div>
          <div class="si-stat si-stat--skip">
            <strong>{{ duplicates.length }}</strong>
            <span>déjà présentes</span>
          </div>
        </div>

        <p class="si-intro" v-if="duplicates.length">
          Une mesure est considérée comme déjà présente quand sa colonne source
          existe déjà ici. Elle est laissée intacte : son paramétrage actuel
          n'est jamais écrasé.
        </p>

        <div v-if="!toImport.length" class="si-empty">
          Toutes les mesures de cet univers existent déjà ici. Rien à importer.
        </div>

        <table v-else class="si-table">
          <thead>
          <tr>
            <th class="si-check-col">
              <input
                  type="checkbox"
                  :checked="allSelected"
                  :indeterminate.prop="someSelected"
                  @change="toggleAll"
              />
            </th>
            <th>Mesure</th>
            <th>Colonne source</th>
            <th>Unité</th>
            <th class="num">Min</th>
            <th class="num">Max</th>
          </tr>
          </thead>
          <tbody>
          <tr
              v-for="sensor in toImport"
              :key="sensor.csv_column + sensor.name"
              :class="{ unchecked: !selected.includes(key(sensor)) }"
          >
            <td class="si-check-col">
              <input
                  type="checkbox"
                  :checked="selected.includes(key(sensor))"
                  @change="toggle(sensor)"
              />
            </td>
            <td>{{ sensor.name }}</td>
            <td><code>{{ sensor.csv_column }}</code></td>
            <td>{{ sensor.unit_of_measure || '—' }}</td>
            <td class="num">{{ sensor.min !== null && sensor.min !== '' ? sensor.min : '—' }}</td>
            <td class="num">{{ sensor.max !== null && sensor.max !== '' ? sensor.max : '—' }}</td>
          </tr>
          </tbody>
        </table>

        <div v-if="duplicates.length" class="si-dupes">
          <span class="si-dupes-label">Ignorées :</span>
          <span v-for="s in duplicates" :key="'d' + s.csv_column" class="si-dupe-chip">
            {{ s.csv_column }}
          </span>
        </div>

        <v-alert dense text type="info" class="si-note">
          Les mesures sont ajoutées au formulaire. Elles ne seront réellement
          enregistrées qu'à la sauvegarde de l'univers.
        </v-alert>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="si-actions">
        <v-btn v-if="step === 2" text @click="step = 1">
          <v-icon left small>arrow_back</v-icon>
          Retour
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('close')">Annuler</v-btn>
        <v-btn
            v-if="step === 1"
            color="#2d5a3d"
            dark
            :disabled="!sourceId || !sourceSensors.length"
            @click="step = 2"
        >
          Continuer
        </v-btn>
        <v-btn
            v-else
            color="#2d5a3d"
            dark
            :disabled="!selected.length"
            @click="confirm"
        >
          <v-icon left small>check</v-icon>
          Importer {{ selected.length }} mesure(s)
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "SensorImportDialog",

  props: {
    open: {type: Boolean, default: false},
    // L'univers en cours d'édition
    site: {type: Object, required: true},
    // Les univers du même client
    clientSites: {type: Array, default: () => []},
  },

  data() {
    return {
      step: 1,
      sourceId: null,
      selected: [],
    };
  },

  computed: {
    candidates() {
      return this.clientSites.filter((s) => s.id !== this.site.id);
    },

    sourceSite() {
      return this.candidates.find((s) => s.id === this.sourceId) || null;
    },

    sourceSensors() {
      return (this.sourceSite && this.sourceSite.sensors) || [];
    },

    /** Colonnes déjà couvertes ici : c'est la clé d'unicité côté import. */
    existingColumns() {
      return (this.site.sensors || [])
          .map((s) => (s.csv_column || "").trim())
          .filter(Boolean);
    },

    toImport() {
      return this.sourceSensors.filter(
          (s) => !this.existingColumns.includes((s.csv_column || "").trim())
      );
    },

    duplicates() {
      return this.sourceSensors.filter(
          (s) => this.existingColumns.includes((s.csv_column || "").trim())
      );
    },

    allSelected() {
      return this.toImport.length > 0 && this.selected.length === this.toImport.length;
    },

    someSelected() {
      return this.selected.length > 0 && this.selected.length < this.toImport.length;
    },
  },

  watch: {
    open(v) {
      if (v) {
        this.step = 1;
        this.sourceId = null;
        this.selected = [];
      }
    },

    // Tout est coché par défaut : c'est le cas d'usage courant.
    step(v) {
      if (v === 2) {
        this.selected = this.toImport.map((s) => this.key(s));
      }
    },
  },

  methods: {
    key(sensor) {
      return (sensor.csv_column || "") + "|" + (sensor.name || "");
    },

    toggle(sensor) {
      const k = this.key(sensor);
      const i = this.selected.indexOf(k);

      if (i >= 0) {
        this.selected.splice(i, 1);
      } else {
        this.selected.push(k);
      }
    },

    toggleAll() {
      this.selected = this.allSelected ? [] : this.toImport.map((s) => this.key(s));
    },

    confirm() {
      const picked = this.toImport.filter((s) => this.selected.includes(this.key(s)));

      // Copie propre : on ne reprend QUE la définition de la mesure.
      // id, site_id et les relations du capteur source sont volontairement
      // laissés de côté, sinon on écrirait par-dessus un capteur existant.
      const copies = picked.map((s) => ({
        name: s.name,
        csv_column: s.csv_column,
        unit_of_measure: s.unit_of_measure || "",
        type: s.type || "",
        min: s.min !== undefined && s.min !== null ? s.min : "",
        max: s.max !== undefined && s.max !== null ? s.max : "",
        sensor_type_id: s.sensor_type_id || null,
      }));

      this.$emit("imported", copies);
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.si-card {
  border-radius: 12px;
}

.si-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2d24;
}

.si-body {
  padding-top: 18px !important;
}

.si-intro {
  font-size: 0.82rem;
  color: #8a9a90;
  margin: 0 0 14px;
}

.si-empty {
  font-size: 0.85rem;
  color: #8a9a90;
  font-style: italic;
  padding: 14px 0;
}

.si-source-info {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  font-size: 0.82rem;
  color: #2d5a3d;
}

.si-source-info .material-symbols-outlined {
  font-size: 18px;
}

.si-summary {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.si-stat {
  flex: 1;
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
}

.si-stat strong {
  font-size: 1.5rem;
  line-height: 1.1;
}

.si-stat span {
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.si-stat--add {
  background: #eef5f0;
  color: #2d5a3d;
}

.si-stat--skip {
  background: #f4f5f4;
  color: #8a9a90;
}

.si-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.si-table th {
  text-align: left;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a9a90;
  font-weight: 600;
  padding: 6px 8px;
  border-bottom: 1px solid #e6ebe8;
}

.si-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #f4f6f5;
  color: #1f2d24;
}

.si-table tr.unchecked td {
  opacity: 0.45;
}

.si-table .num {
  text-align: right;
  width: 70px;
}

.si-check-col {
  width: 36px;
}

.si-table code {
  background: #f0f3f1;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 0.74rem;
  color: #2d5a3d;
}

.si-dupes {
  margin-top: 14px;
  font-size: 0.76rem;
  color: #8a9a90;
}

.si-dupes-label {
  margin-right: 6px;
}

.si-dupe-chip {
  display: inline-block;
  background: #f0f3f1;
  border-radius: 4px;
  padding: 1px 7px;
  margin: 0 4px 4px 0;
  font-family: monospace;
  font-size: 0.72rem;
}

.si-note {
  margin-top: 16px;
  font-size: 0.78rem;
}

.si-actions {
  padding: 12px 16px;
}
</style>
